<script setup lang="ts">
  // Details behind the two "sin efecto" counters of a caja close card:
  // cancelled orders and cancelled lines, both frozen in the close snapshot.
  // Two identical disclosures, collapsed by default; a section only renders
  // when it has rows.
  import { computed, getCurrentInstance, reactive } from 'vue';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import { OrderType } from '@/enums/OrderEnums';
  import {
    ICajaCanceledItemDetail,
    OrderDetails,
  } from '@/interfaces/ICajaResume';
  import { displayPrice } from '@/utils';

  type SectionKey = 'orders' | 'items';

  interface IDetailRow {
    key: string;
    /** Muted reference: "#28655 · 08/09 15:43" or "Mesa 11 · #28652 · 08/09 15:13". */
    meta: string;
    title: string;
    /** Muted line under the title (cancelled orders: their lines inline). */
    secondary: string;
    total: string;
  }

  interface IDetailSection {
    key: SectionKey;
    icon: string;
    title: string;
    badge: number;
    rows: IDetailRow[];
    /** Footnote under the rows (cancelled lines: the omitted-units note). */
    note: string;
  }

  const ORDER_TYPE_KEYS: Record<OrderType, string> = {
    [OrderType.Seat]: 'order.type.seat',
    [OrderType.Away]: 'order.type.away',
    [OrderType.Delivery]: 'order.type.delivery',
  };

  // One formatter for every row: toLocaleString would build one per call.
  const TIME_FORMATTER = new Intl.DateTimeFormat(undefined, {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

  let cardCounter = 0;

  const props = defineProps<{
    orders: OrderDetails[];
    items: ICajaCanceledItemDetail[];
    omittedUnits: number;
    currencySymbol: string;
  }>();

  const proxy = getCurrentInstance()!.proxy;
  const expanded = reactive<Record<SectionKey, boolean>>({
    orders: false,
    items: false,
  });
  // Unique per card so each toggle's aria-controls points at its own list.
  const listIdPrefix = `caja-canceled-${++cardCounter}`;

  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  function listId(section: SectionKey): string {
    return `${listIdPrefix}-${section}`;
  }

  function toggle(section: SectionKey): void {
    expanded[section] = !expanded[section];
  }

  /** Trims the parts, drops the empty ones and joins the rest. */
  function joinParts(
    parts: Array<string | null | undefined>,
    separator = ' · '
  ): string {
    return parts
      .map((part) => (part ?? '').trim())
      .filter((part) => part.length > 0)
      .join(separator);
  }

  /** "08/09 15:43"; empty for the `0` fallback of POS snapshots. */
  function formatTime(value: string | Date | number): string {
    if (!value) return '';
    return TIME_FORMATTER.format(new Date(value));
  }

  function formatPrice(total: number): string {
    return `${props.currencySymbol} ${displayPrice(total)}`;
  }

  function tableLabel(name: string): string {
    // posV2.tableWithName carries the restaurant-table sense in every locale
    // (the bare `table` key means a data table in some languages).
    return i18nT('posV2.tableWithName', { name });
  }

  /**
   * "Mesa 10" when the label was frozen in the snapshot (table orders only),
   * else the order type word ("Take away", "Delivery"); empty when unknown.
   */
  function orderWhere(order: OrderDetails): string {
    if (order.tableName) return tableLabel(order.tableName);
    const typeKey = order.type ? ORDER_TYPE_KEYS[order.type] : undefined;
    return typeKey ? i18nT(typeKey) : '';
  }

  /** "Mesa 10 · Alejandro", "Delivery · Juan, Calle 123". */
  function orderTitle(order: OrderDetails): string {
    const customer = joinParts(
      [order.customerName, order.customerAddress],
      ', '
    );
    return joinParts([orderWhere(order), customer]);
  }

  /** "1× Cordero de prueba, 2× Arroz de prueba". */
  function orderItemsInline(order: OrderDetails): string {
    return (order.items ?? [])
      .map((item) => `${item.quantity}× ${item.itemName}`)
      .join(', ');
  }

  // Index-suffixed keys: POS-built snapshots may repeat ids.
  const orderRows = computed<IDetailRow[]>(() =>
    props.orders.map((order, index) => ({
      key: `${order.id}-${index}`,
      meta: joinParts([`#${order.id}`, formatTime(order.cancelledAt)]),
      title: orderTitle(order),
      secondary: orderItemsInline(order),
      total: formatPrice(order.total),
    }))
  );

  const itemRows = computed<IDetailRow[]>(() =>
    props.items.map((item, index) => ({
      key: `${item.orderRef}-${item.itemId}-${index}`,
      meta: joinParts([
        item.tableName ? tableLabel(item.tableName) : '',
        item.orderRef,
        formatTime(item.canceledAt),
      ]),
      title: `${item.quantity}× ${item.itemName}`,
      secondary: '',
      total: formatPrice(item.total),
    }))
  );

  /** Units, not rows: matches the "Productos cancelados" stat above the card. */
  const listedUnits = computed(() =>
    props.items.reduce((acc, item) => acc + item.quantity, 0)
  );

  const omittedLabel = computed(() =>
    props.omittedUnits > 0
      ? String(
          proxy.$tc('v2Stats.cajas.canceledItemsOmitted', props.omittedUnits, {
            n: props.omittedUnits,
          })
        )
      : ''
  );

  const sections = computed<IDetailSection[]>(() =>
    [
      {
        key: 'orders' as const,
        icon: 'delete-outline',
        title: i18nT('v2Stats.cajas.canceledOrdersDetails'),
        badge: props.orders.length,
        rows: orderRows.value,
        note: '',
      },
      {
        key: 'items' as const,
        icon: 'close-box-multiple-outline',
        title: i18nT('v2Stats.cajas.canceledItemsDetails'),
        badge: listedUnits.value,
        rows: itemRows.value,
        note: omittedLabel.value,
      },
    ].filter((section) => section.rows.length > 0)
  );
</script>

<template>
  <section class="canceled-details">
    <div v-for="section in sections" :key="section.key" class="detail">
      <!-- Explicit 'true'/'false': Vue 2 drops attributes bound to false. -->
      <button
        type="button"
        class="toggle"
        :aria-expanded="expanded[section.key] ? 'true' : 'false'"
        :aria-controls="expanded[section.key] ? listId(section.key) : undefined"
        @click="toggle(section.key)"
      >
        <span class="toggle-left">
          <PosUiIcon :name="section.icon" class="toggle-icon" aria-hidden="true" />
          <span class="toggle-title">{{ section.title }}</span>
          <PosUiBadge :label="section.badge" class="toggle-count" />
        </span>
        <PosUiIcon
          :name="expanded[section.key] ? 'chevron-up' : 'chevron-down'"
          class="toggle-chevron"
          aria-hidden="true"
        />
      </button>

      <div
        v-if="expanded[section.key]"
        :id="listId(section.key)"
        class="list"
      >
        <div v-for="row in section.rows" :key="row.key" class="line">
          <span class="line-meta">{{ row.meta }}</span>
          <span class="line-main">
            <span v-if="row.title" class="line-title">{{ row.title }}</span>
            <span v-if="row.secondary" class="line-secondary">
              {{ row.secondary }}
            </span>
          </span>
          <span class="line-total">{{ row.total }}</span>
        </div>
        <p v-if="section.note" class="omitted">{{ section.note }}</p>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
  .canceled-details {
    // Same hairline as the card's own header divider.
    @apply tw-border-0 tw-border-t tw-border-solid tw-border-neutral-100 tw-px-4 tw-py-2;

    .detail + .detail {
      @apply tw-border-0 tw-border-t tw-border-solid tw-border-neutral-100;
    }

    .toggle {
      @apply tw-flex tw-h-11 tw-w-full tw-items-center tw-justify-between tw-rounded-lg tw-border-0 tw-bg-transparent tw-px-1 tw-text-left tw-text-sm tw-font-medium tw-text-neutral-700 tw-transition-colors hover:tw-bg-neutral-50;

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }

      .toggle-left {
        @apply tw-flex tw-min-w-0 tw-items-center;

        // margins instead of flex gap: unsupported on Chrome <84.
        .toggle-icon {
          @apply tw-mr-2 tw-text-lg tw-text-neutral-400;
        }

        .toggle-title {
          @apply tw-min-w-0 tw-truncate;
        }

        .toggle-count {
          @apply tw-ml-2;
        }
      }

      .toggle-chevron {
        @apply tw-ml-2 tw-text-lg tw-text-neutral-400;
      }
    }

    .list {
      @apply tw-pb-1;

      // Not `.row`: Vuetify's global .row would bleed a -12px margin in.
      // Same horizontal inset as the toggle so texts align.
      .line {
        @apply tw-grid tw-gap-x-3 tw-border-0 tw-border-t tw-border-solid tw-border-neutral-100 tw-px-1 tw-py-1.5;
        grid-template-columns: minmax(0, 1fr) auto;
        grid-template-areas:
          'meta meta'
          'main total';

        @screen sm {
          // The meta column never squeezes the main text: it caps at half
          // the row and ellipsizes (labels can be 60 chars).
          grid-template-columns: fit-content(50%) minmax(0, 1fr) auto;
          grid-template-areas: 'meta main total';
        }

        .line-meta {
          // Wraps on phones (the order ref and time must stay visible);
          // ellipsizes only once it has its own column.
          @apply tw-min-w-0 tw-text-xs tw-text-neutral-500;
          grid-area: meta;

          @screen sm {
            @apply tw-truncate;
          }
        }

        .line-main {
          @apply tw-flex tw-min-w-0 tw-flex-col;
          grid-area: main;

          .line-title {
            @apply tw-text-sm tw-text-neutral-900;
          }

          .line-secondary {
            @apply tw-text-xs tw-text-neutral-500;
          }
        }

        .line-total {
          @apply tw-text-right tw-text-sm tw-font-semibold tw-tabular-nums tw-text-neutral-900;
          grid-area: total;
        }
      }

      .omitted {
        @apply tw-mb-0 tw-px-1 tw-pt-2 tw-text-xs tw-text-neutral-500;
      }
    }
  }
</style>
